import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Row, Col } from 'antd'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { FieldTypeEnum, type CheckboxFieldConfig } from '../../../../../core/components/InputManager/types.ts'
import { useLookups } from '../../../../global_configs/hooks/useLookups.ts'
import { useDispatch, useSelector } from 'react-redux'
import { updateCompanyRecord } from '../../../../../state_manager/state_update_actions/companies/index.ts'
import { AccountRecordFields } from '../../../../../state_manager/data_models/account_record.ts'
import { UserEntryFields } from '../../../../../state_manager/data_models/user_entry.ts'
import { UNKNOWN_CONTACT } from '../../../../../state_manager/constants/strings.ts'
import {
  getCompanyDetailsFields,
  getBusinessAddressFields,
  getOfficeAddressFields,
  getGeneralFields,
  type SourcingContactsByRole
} from '../../../utils/fieldConfigs.ts'
import { AccountRecord } from '../../../../../state_manager/data_models/account_record.ts'
import { FundingRoundsTable } from '../../tables/FundingRoundsTable/index.tsx'
import { ShortDescriptionSection } from './ShortDescriptionSection.tsx'
import { accountHasPortfolioRole } from '../../../utils/accountRoles.ts'
import { buildAccountRoleOptions } from '../../../../../core/utils/accountRoleOptions.ts'

export function DetailsTab({
  currentCompany,
  formData,
  handleFieldChange,
  onPatchSuccess,
  recordId
}: {
  currentCompany?: AccountRecord | null
  formData?: Record<string, any>
  handleFieldChange?: (field: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
  recordId?: string | null
}) {
  const { getLookupOptions, detailsByCategory } = useLookups()
  const companyLookupId =
    detailsByCategory?.['Global Configs']?.find((option: any) => option.label === 'internal_company_id')?.value || null
  const dispatch = useDispatch()

  const accountRoleChoices = useSelector((state: any) => state.globalConfigs?.account_role_choices || [])
  const investorClassifications = useSelector((state: any) => state.globalConfigs?.investor_classifications || [])
  const shareRegisters = useSelector((state: any) => state.globalConfigs?.share_registers || [])
  const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])

  const getRoleOptions = useCallback(
    () => buildAccountRoleOptions(accountRoleChoices),
    [accountRoleChoices]
  )

  const getInvestorClassificationOptions = useCallback(() => {
    return investorClassifications.map((classification: any) => ({
      value: classification.id,
      label: classification.label
    }))
  }, [investorClassifications])

  const getShareRegisterOptions = useCallback(() => {
    return shareRegisters.map((register: any) => ({
      value: register.id,
      label: register.label
    }))
  }, [shareRegisters])

  const data = currentCompany || formData || {}

  // Funding Rounds is portfolio-only — hidden for accounts without a
  // Portfolio Company / Exited / Dead role (matches the other portfolio sections).
  const showPortfolioSections = accountHasPortfolioRole(currentCompany)

  const companyId =
    recordId || currentCompany?.[AccountRecordFields.accounts__id] || data[AccountRecordFields.accounts__id] || data.id

  const [selectedContacts, setSelectedContacts] = useState<{
    introduced_by: any[]
    sourced_by: any[]
  }>({
    introduced_by: [],
    sourced_by: []
  })

  const initialSameAsBusinessAddress =
    data[AccountRecordFields.accounts__same_as_business_address] !== undefined
      ? data[AccountRecordFields.accounts__same_as_business_address]
      : true

  const [sameAsBusinessAddress, setSameAsBusinessAddress] = useState(initialSameAsBusinessAddress)

  useEffect(() => {
    const newValue =
      data[AccountRecordFields.accounts__same_as_business_address] !== undefined
        ? data[AccountRecordFields.accounts__same_as_business_address]
        : true
    setSameAsBusinessAddress(newValue)
  }, [data[AccountRecordFields.accounts__same_as_business_address]])

  useEffect(() => {
    const introducedByDetail = data[AccountRecordFields.contact_accounts__introduced_by] || data.introduced_by_detail
    const sourcedByDetails = data[AccountRecordFields.contact_accounts__sourced_by] || data.sourced_by_details

    const contactsByRole: SourcingContactsByRole = {
      introduced_by: [],
      sourced_by: []
    }

    if (introducedByDetail && Array.isArray(introducedByDetail)) {
      contactsByRole.introduced_by = introducedByDetail.map((contact: any) => ({
        value: contact[UserEntryFields.contacts__id] || contact.id || '',
        label:
          contact[UserEntryFields.prog__display_name] ||
          `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
          contact[UserEntryFields.contacts__email] ||
          UNKNOWN_CONTACT,
        email: contact[UserEntryFields.contacts__email] || '',
        data: contact
      }))
    }

    if (sourcedByDetails && Array.isArray(sourcedByDetails)) {
      contactsByRole.sourced_by = sourcedByDetails.map((contact: any) => ({
        value: contact[UserEntryFields.contacts__id] || contact.id || '',
        label:
          contact[UserEntryFields.prog__display_name] ||
          `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
          contact[UserEntryFields.contacts__email] ||
          UNKNOWN_CONTACT,
        email: contact[UserEntryFields.contacts__email] || '',
        data: contact
      }))
    }

    setSelectedContacts(contactsByRole)
  }, [
    data[AccountRecordFields.contact_accounts__introduced_by],
    data[AccountRecordFields.contact_accounts__sourced_by],
    data.introduced_by_detail,
    data.sourced_by_details
  ])

  const handlePatchSuccessLocal = async (patchRecordId: string, updates: Record<string, any>) => {
    if (onPatchSuccess) {
      onPatchSuccess(patchRecordId, updates)
    } else {
      dispatch(
        updateCompanyRecord({
          companyId: patchRecordId,
          updates
        })
      )
    }
  }

  const helpers = useMemo(
    () => ({
      getLookupOptions: (category: string) => {
        return getLookupOptions(category)
      },
      getRoleOptions,
      getInvestorClassificationOptions,
      getShareRegisterOptions,
      countriesData
    }),
    [getLookupOptions, getRoleOptions, getInvestorClassificationOptions, getShareRegisterOptions, countriesData]
  )

  const companyInfoFields = getCompanyDetailsFields(data, helpers)
  const businessAddressFields = getBusinessAddressFields(data, helpers, false)
  const officeAddressFields = getOfficeAddressFields(data, helpers, false)
  const generalFields = getGeneralFields(data, selectedContacts, undefined, companyLookupId)

  const handleCheckboxChange = (key: string, value: any) => {
    setSameAsBusinessAddress(value)
    handleFieldChange?.(key, value)
  }

  const checkboxField = useMemo<CheckboxFieldConfig>(
    () => ({
      key: AccountRecordFields.accounts__same_as_business_address,
      type: FieldTypeEnum.CHECKBOX,
      label: '',
      value: sameAsBusinessAddress,
      checkboxLabel: 'Same as business address',
      editable: true
    }),
    [sameAsBusinessAddress]
  )

  return (
    <div
      data-debug='company-details-tab-container'
      data-component='DetailsTab'
      data-record-id={recordId}
      className='h-full'
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <div className='space-y-4'>
            <FormSection title='Company Details' titleColor='blue'>
              <div data-debug='company-details-content'>
                <InputManager
                  inputMode='read'
                  labelPosition='inline'
                  fields={companyInfoFields}
                  onChange={(key, value) => {
                    if (key === AccountRecordFields.contact_accounts__introduced_by) {
                      setSelectedContacts((prev) => ({
                        ...prev,
                        introduced_by: Array.isArray(value) ? value : []
                      }))
                    } else if (key === AccountRecordFields.contact_accounts__sourced_by) {
                      setSelectedContacts((prev) => ({
                        ...prev,
                        sourced_by: Array.isArray(value) ? value : []
                      }))
                    }
                    handleFieldChange?.(key, value)
                  }}
                  enableAutoPatch={!!companyId}
                  patchId={companyId}
                  recordId={companyId}
                  onPatchSuccess={handlePatchSuccessLocal}
                  className='company-details-fields'
                  fieldWrapping={true}
                  currentRecord={currentCompany || data}
                />
              </div>
            </FormSection>

            {/* Reports print it for portfolio companies only. */}
            {showPortfolioSections && companyId ? <ShortDescriptionSection accountId={companyId} /> : null}

            {showPortfolioSections ? (
              <FundingRoundsTable
                companyData={currentCompany || formData || data}
                recordId={companyId}
                handleFieldChange={handleFieldChange}
                onPatchSuccess={handlePatchSuccessLocal}
              />
            ) : null}
          </div>
        </Col>

        <Col xs={24} lg={10}>
          <div className='space-y-4'>
            <FormSection title='Business Address' titleColor='blue'>
              <InputManager
                inputMode='read'
                labelPosition='inline'
                fields={businessAddressFields}
                onChange={(key, value) => handleFieldChange?.(key, value)}
                enableAutoPatch={!!companyId}
                patchId={companyId}
                recordId={companyId}
                onPatchSuccess={handlePatchSuccessLocal}
                className='business-address-fields'
                fieldWrapping={true}
              />
            </FormSection>

            <FormSection title='Registered Office' titleColor='blue'>
              <div>
                <div className='mb-3'>
                  <InputManager
                    inputMode='edit'
                    labelPosition='inline'
                    fields={[checkboxField]}
                    onChange={handleCheckboxChange}
                    enableAutoPatch={!!companyId}
                    patchId={companyId}
                    recordId={companyId}
                    onPatchSuccess={handlePatchSuccessLocal}
                    className='registered-office-checkbox'
                  />
                </div>

                {!sameAsBusinessAddress && (
                  <InputManager
                    inputMode='read'
                    labelPosition='inline'
                    fields={officeAddressFields}
                    onChange={(key, value) => {
                      handleFieldChange?.(key, value)
                    }}
                    enableAutoPatch={!!companyId}
                    patchId={companyId}
                    recordId={companyId}
                    onPatchSuccess={handlePatchSuccessLocal}
                    className='office-address-fields'
                    fieldWrapping={true}
                  />
                )}
              </div>
            </FormSection>
          </div>
        </Col>
      </Row>
    </div>
  )
}
